import { MouseEvent, useEffect, useRef, useState } from 'react';
import { escapeRegExp } from 'lodash';
import { IonButton, IonContent, IonInput, IonItem, IonLabel, IonList, IonModal, IonSearchbar, isPlatform } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { ISearchSelectOption, ISelected, ISearchableSelectFieldProps } from './SearchableSelectField.tsx';
import { CloseIcon, SearchIcon } from '../icons/Icons.tsx';
import { PageHeader } from '../page/PageHeader.tsx';
import { RequiredTrigger } from './RequiredTrigger.tsx';
import { DeviceStatusBar } from '../../support/DeviceStatusBar.ts';
import { useInstance } from '@meraki-internal/react-dependency-injection';

const useStyles = createUseStyles({
    input: {
        '& .native-input': {
            cursor: 'pointer',
            maxWidth: 'calc(100% - 45px)', // account for search icon and delete button
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }
    }
});

interface ITypeaheadProps<T> {
    allowCustom?: boolean;
    noInputMessage: string;
    placeholder?: string;
    options?: ISearchSelectOption<T>[];
    loadOptions?: (text: string, callback: (options: ISearchSelectOption<T>[]) => void) => void;
    onSelect: (option: ISelected<T>) => void;
}

const Typeahead = <T,>({
    allowCustom,
    noInputMessage,
    placeholder,
    options,
    loadOptions,
    onSelect
}: ITypeaheadProps<T>) => {
    const statusBar = useInstance(DeviceStatusBar);

    const searchBar = useRef<HTMLIonSearchbarElement>(null);
    const searchRef = useRef<string>('');

    const [filteredOptions, setFilteredOptions] = useState<ISelected<T>[]>(options || []);

    useEffect(() => {
        // don't set focus if we already have a list to select from
        if (!options) {
            // ios removes focus at about 1000ms, so it needs a very long timeout
            const timeoutMs = isPlatform('ios') ? 1500 : 300;

            setTimeout(() => {
                searchBar.current?.setFocus();
            }, timeoutMs);
        }
    }, [options, searchBar]);

    const onInput = async (search: string | null | undefined) => {
        searchRef.current = search || '';

        if (!search) {
            setFilteredOptions(options || []);
        } else {
            const normalizedQuery = search.toLowerCase();

            const buildOptions = (_options: ISearchSelectOption<T>[]) => {
                setFilteredOptions([
                    ..._options,
                    // add "enter your own" option at the end if none match exactly
                    ..._options.find(o => o.label === search) ? [] : allowCustom ? [{label: search, value: undefined}] : []
                ]);
            };

            if (options) {
                buildOptions(options.filter(o => o.label.toLowerCase().includes(normalizedQuery)));
            } else {
                loadOptions!(normalizedQuery, _options => {
                    if (searchBar.current?.value) {
                        buildOptions(_options);
                    }
                });
            }
        }
    };

    const formatOptionLabel = (option: ISelected<T>) => {
        if (!searchRef.current) {
            return option.label;
        }
        const escapedInput = escapeRegExp(searchRef.current);
        let formatted = option.label.replace(
            new RegExp(escapedInput, 'gi'),
            highlighted => `<strong>${highlighted}</strong>`
        );

        if (allowCustom && option.value === undefined) {
            formatted += ' (enter your own)';
        }
        return (
            <span dangerouslySetInnerHTML={{ __html: formatted }} />
        );
    };

    return (
        <div style={{height: '100%', overflowY: 'hidden'}}>
            <div style={{paddingLeft: 10, paddingRight: 10}}>
                <IonSearchbar ref={searchBar} mode="ios" onIonInput={e => onInput(e.target.value)} placeholder={placeholder} />
            </div>
            <IonContent>
                {!options && filteredOptions && !searchBar.current?.value && !filteredOptions.length &&
                    <p style={{fontSize: 14, marginLeft: 20}}><i>{noInputMessage}</i></p>
                }

                {/* need extra 60px to account for fixed search bar at the top */}
                <IonList id="modal-list" style={{paddingBottom: 60 + statusBar.getNavigationBarHeight()}}>
                    {filteredOptions.map((option) => (
                        <IonItem key={option.label} style={{fontSize: 15}} onClick={() => onSelect(option)}>
                            {formatOptionLabel(option)}
                        </IonItem>
                    ))}
                </IonList>
            </IonContent>
        </div>
    );
};

export const SearchableSelectFieldMobile = <T,>({
    'data-type': dataType,
    label,
    placeholder,
    required = false,
    allowCustom = false,
    readonly = false,
    noInputMessage = '',
    selected,
    onChange,
    options,
    loadOptions
}: ISearchableSelectFieldProps<T>) => {
    const classes = useStyles();

    const inputRef = useRef<HTMLIonInputElement>(null);

    const [isLookupOpen, setLookupOpen] = useState(false);

    const openLookup = () => {
        setLookupOpen(true);
    };

    const closeLookup = () => {
        // ionic seems to get confused when the modal closes, and leaves the
        // readonly IonInput looking like it has focus, so we remove the class manually
        inputRef.current?.classList.remove('has-focus');
        setLookupOpen(false);
    };

    const onLookupCanceled = () => {
        // if we close the modal while adding, remove the blank item
        if (!selected) {
            onChange(undefined);
        }
        closeLookup();
    };

    const onItemSelected = (item: ISelected<T>) => {
        onChange(item);
        closeLookup();
    };

    const onItemRemoved = (e: MouseEvent) => {
        e.stopPropagation();
        onChange(undefined);
    };

    const labelPosition = (readonly ? 'stacked' : 'floating');

    return (
        <>
            {label &&
                <IonLabel color="medium" position={labelPosition}>{label}{required ? '*' : ''}</IonLabel>
            }
            <IonInput
                ref={inputRef}
                data-type={dataType}
                required={required}
                readonly={true}
                className={readonly ? undefined : classes.input}
                value={selected?.label || ''}
                onClick={!readonly ? openLookup : undefined}
            >
                {required &&
                    <RequiredTrigger value={selected?.label || ''} />
                }
            </IonInput>

            <div style={{display: 'flex', alignItems: 'center', position: 'absolute', bottom: 0, right: 0, zIndex: 1000}}>
                {selected && !readonly &&
                    <IonButton fill="clear" size="small" onClick={onItemRemoved}>
                        <CloseIcon color="medium" />
                    </IonButton>
                }
                {!readonly &&
                    <span style={{pointerEvents: 'none'}}>
                        <SearchIcon size="medium" color="medium" />
                    </span>
                }
            </div>

            <IonModal
                isOpen={isLookupOpen}
                onWillDismiss={onLookupCanceled}
            >
                <PageHeader title={label} onClose={onLookupCanceled} />
                <Typeahead
                    allowCustom={allowCustom}
                    placeholder={placeholder}
                    noInputMessage={noInputMessage}
                    options={options}
                    loadOptions={loadOptions}
                    onSelect={onItemSelected}
                />
            </IonModal>

        </>
    );
};
